import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import Link from "@mui/material/Link";
import InputLabel from "@mui/material/InputLabel";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import BlockIcon from "@mui/icons-material/Block";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import FolderIcon from "@mui/icons-material/Folder";
import DriveExplorer from "../DriveExplorer";
import { Autocomplete, createFilterOptions, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch } from "@mui/material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import ContactModal from "../ContactModal";
import ServiceOrderDialog from "../ServiceOrderDialog";
import ServiceOrderPrint from "../ServiceOrderPrint";
import PixChargeDialog from "../PixChargeDialog";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import QuoteDialog from "../QuoteDialog";
import ReceituarioDialog from "../ReceituarioDialog";
import AdesivoDialog from "../AdesivoDialog";
import CartelaDialog from "../CartelaDialog";
import QuotePrint from "../QuotePrint";
import EngineeringIcon from "@mui/icons-material/Engineering";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";
import SaveIcon from "@mui/icons-material/Save";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import PrintIcon from "@mui/icons-material/Print";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";

const drawerWidth = 320;

const TAG_COLORS = [
  "#F37021", "#273A74", "#0071BC", "#C23582",
  "#F4C02F", "#555A5F", "#27AE60", "#E74C3C",
];

const useStyles = makeStyles(theme => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    display: "flex",
    borderTop: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    backgroundColor: theme.palette.background.default,
  },
  header: {
    display: "flex",
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    minHeight: "73px",
    justifyContent: "flex-start",
  },
  content: {
    display: "flex",
    backgroundColor: theme.palette.background.default,
    flexDirection: "column",
    padding: "8px 0px 8px 8px",
    height: "100%",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  contactAvatar: {
    margin: 15,
    width: 180,
    height: 180,
  },
  contactHeader: {
    display: "flex",
    padding: 8,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    "& > *": {
      margin: 4,
    },
  },
  contactDetails: {
    marginTop: 8,
    padding: 8,
    display: "flex",
    flexDirection: "column",
  },
  contactExtraInfo: {
    marginTop: 4,
    padding: 6,
  },
  tagsSection: {
    marginTop: 8,
    padding: 8,
    display: "flex",
    flexDirection: "column",
  },
  tagsSectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginBottom: 8,
    color: theme.palette.text.secondary,
  },
  tagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 8,
  },
  addTagRow: {
    display: "flex",
    gap: 6,
    alignItems: "center",
    marginTop: 4,
  },
  colorDots: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
    marginTop: 4,
  },
  colorDot: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    cursor: "pointer",
    border: "2px solid transparent",
    "&:hover": { opacity: 0.8 },
  },
}));

const tagFilter = createFilterOptions();

const ContactDrawer = ({ open, handleDrawerClose, contact, loading, whatsappId, isGroup, ticketId }) => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tagInput, setTagInput] = useState(null);
  const [tagInputColor, setTagInputColor] = useState("#F37021");
  const [showAddTag, setShowAddTag] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addMemberNum, setAddMemberNum] = useState("");
  const [businessProfile, setBusinessProfile] = useState(null);
  const [bizLoading, setBizLoading] = useState(false);
  const [contactAbout, setContactAbout] = useState(null);
  const [contactTickets, setContactTickets] = useState([]);
  const [waLabels, setWaLabels] = useState([]);
  const [applyingLabel, setApplyingLabel] = useState(null);
  const [leavingGroup, setLeavingGroup] = useState(false);
  const [commonGroups, setCommonGroups] = useState([]);
  const [groupDescription, setGroupDescription] = useState("");
  const [groupSubject, setGroupSubject] = useState("");
  const [editingSubject, setEditingSubject] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const [adminsOnly, setAdminsOnly] = useState(false);
  const [temporaryMessages, setTemporaryMessages] = useState(false);
  const [savingGroupSettings, setSavingGroupSettings] = useState(false);
  const [joinLinkOpen, setJoinLinkOpen] = useState(false);
  const [joinLinkValue, setJoinLinkValue] = useState("");
  const [blingData, setBlingData] = useState(null);
  const [blingLoading, setBlingLoading] = useState(false);
  const [blingEnabled, setBlingEnabled] = useState(false);
  const [blingSearch, setBlingSearch] = useState("");
  const [blingSearchResults, setBlingSearchResults] = useState([]);
  const [blingSearchLoading, setBlingSearchLoading] = useState(false);
  const EMPTY_FISCAL = { cpf:"", cnpj:"", email:"", celular:"", dataNascimento:"", logradouro:"", numero:"", complemento:"", bairro:"", municipio:"", uf:"", cep:"" };
  const [fiscalData, setFiscalData] = useState(EMPTY_FISCAL);
  const [fiscalLoaded, setFiscalLoaded] = useState(false);
  const [fiscalLoading, setFiscalLoading] = useState(false);
  const [savingFiscal, setSavingFiscal] = useState(false);
  const [extractingFiscal, setExtractingFiscal] = useState(false);
  const [chatExtractFiscal, setChatExtractFiscal] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatViewOpen, setChatViewOpen] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [updatingBlingName, setUpdatingBlingName] = useState(false);
  const [soDialogOpen, setSoDialogOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [recDialogOpen, setRecDialogOpen] = useState(false);
  const [adesivoDialogOpen, setAdesivoDialogOpen] = useState(false);
  const [cartelaDialogOpen, setCartelaDialogOpen] = useState(false);
  const [sendConfirm, setSendConfirm] = useState(null); // { type: 'so'|'quote', entity, printOpen: false }
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [pixOrder, setPixOrder] = useState(null);

  useEffect(() => {
    if (!open || !whatsappId) { setWaLabels([]); return; }
    api.get(`/whatsapp/${whatsappId}/labels`)
      .then(({ data }) => setWaLabels(Array.isArray(data) ? data : []))
      .catch(() => setWaLabels([]));
  }, [open, whatsappId]);

  useEffect(() => {
    if (!contact?.id || !whatsappId || isGroup) return;
    setContactAbout(null);
    api.get(`/contacts/${contact.id}/about`, { params: { whatsappId } })
      .then(({ data }) => setContactAbout(data.about || null))
      .catch(() => {});
  }, [contact?.id, whatsappId, isGroup]);

  useEffect(() => {
    if (!open || !contact?.id) return;
    api.get("/tickets", { params: { contactIdFilter: contact.id, showAll: "true", pageNumber: "1" } })
      .then(({ data }) => setContactTickets(data.tickets || []))
      .catch(() => setContactTickets([]));
  }, [open, contact?.id]);

  useEffect(() => {
    if (!contact?.id) return;
    api.get(`/contacts/${contact.id}/tags`)
      .then(({ data }) => setTags(data))
      .catch(toastError);
  }, [contact?.id]);

  useEffect(() => {
    api.get("/tags/all")
      .then(({ data }) => setAllTags(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const resolveTagName = (val) =>
    !val ? "" : typeof val === "string" ? val : val.name || "";

  const isNewTag = (val) => {
    const name = resolveTagName(val).trim().toLowerCase();
    return name !== "" && !allTags.find(t => t.name.toLowerCase() === name);
  };

  const handleAddTag = async () => {
    const name = resolveTagName(tagInput).trim();
    if (!name) return;
    const color = tagInputColor;
    try {
      const { data } = await api.post(`/contacts/${contact.id}/tags`, { name, color });
      setTags(prev => [...prev, data]);
      setTagInput(null);
      setTagInputColor("#F37021");
      setShowAddTag(false);
      api.get("/tags/all").then(({ data: d }) => setAllTags(Array.isArray(d) ? d : [])).catch(() => {});
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      setTags(prev => prev.filter(t => t.id !== tagId));
    } catch (err) {
      toastError(err);
    }
  };

  const loadGroupMembers = useCallback(async () => {
    if (!isGroup || !contact?.number || !whatsappId) return;
    setMembersLoading(true);
    try {
      const groupId = `${contact.number}@g.us`;
      const { data } = await api.get(`/groups/${encodeURIComponent(groupId)}/members`, {
        params: { whatsappId },
      });
      setGroupMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setMembersLoading(false);
    }
  }, [isGroup, contact?.number, whatsappId]);

  useEffect(() => {
    if (open && isGroup) loadGroupMembers();
  }, [open, isGroup, loadGroupMembers]);

  useEffect(() => {
    if (!open || !contact?.id || isGroup || !whatsappId) { setCommonGroups([]); return; }
    api.get(`/contacts/${contact.id}/common-groups`, { params: { whatsappId } })
      .then(({ data }) => setCommonGroups(Array.isArray(data) ? data : []))
      .catch(() => setCommonGroups([]));
  }, [open, contact?.id, isGroup, whatsappId]);

  useEffect(() => {
    if (user?.profile !== "admin") return;
    api.get("/bling/status").then(({ data }) => setBlingEnabled(data.connected)).catch(() => {});
  }, [user?.profile]);

  useEffect(() => {
    // LGPD/Fase 3.3: dados Bling (pedidos/financeiro) só para admin/supervisor —
    // o backend já bloqueia (403); aqui evitamos a chamada inútil p/ atendente.
    const canViewBling = ["admin", "supervisor"].includes(user?.profile);
    // Reset dos dados fiscais/extração ao trocar de contato (não vazar entre clientes).
    setFiscalData(EMPTY_FISCAL);
    setFiscalLoaded(false);
    setChatExtractFiscal(null);
    setChatMessages([]);
    if (!open || !contact?.id || isGroup || !blingEnabled || !canViewBling) { setBlingData(null); return; }
    setBlingLoading(true);
    api.get(`/bling/contact/${contact.id}/data`)
      .then(({ data }) => setBlingData(data))
      .catch(() => setBlingData(null))
      .finally(() => setBlingLoading(false));
  }, [open, contact?.id, isGroup, blingEnabled, user?.profile]);

  const handleSendBlingOrder = async (orderId) => {
    if (!ticketId || !orderId) return;
    try {
      await api.post(`/bling/ticket/${ticketId}/send-order/${orderId}`);
      toast.success("Pedido enviado no chat.");
    } catch (err) { toastError(err); }
  };

  const handleSendBlingNF = async (orderId) => {
    if (!ticketId || !orderId) return;
    try {
      await api.post(`/bling/ticket/${ticketId}/send-nf/${orderId}`);
      toast.success("Nota fiscal enviada no chat.");
    } catch (err) { toastError(err); }
  };

  const handleBlingSearch = async (nome) => {
    setBlingSearch(nome);
    if (nome.length < 2) { setBlingSearchResults([]); return; }
    setBlingSearchLoading(true);
    try {
      const { data } = await api.get("/bling/contacts/search", { params: { nome } });
      setBlingSearchResults(Array.isArray(data) ? data : []);
    } catch { setBlingSearchResults([]); }
    finally { setBlingSearchLoading(false); }
  };

  const handleLinkBlingContact = async (blingContactId) => {
    if (!contact?.id) return;
    try {
      await api.put(`/bling/contact/${contact.id}/link`, { blingContactId });
      toast.success("Contato vinculado ao Bling.");
      setBlingSearch("");
      setBlingSearchResults([]);
      setFiscalLoaded(false);
      setBlingLoading(true);
      api.get(`/bling/contact/${contact.id}/data`)
        .then(({ data }) => setBlingData(data))
        .catch(() => setBlingData(null))
        .finally(() => setBlingLoading(false));
    } catch (err) { toastError(err); }
  };

  const handleUpdateBlingName = async () => {
    if (!contact?.id || !contact?.name) return;
    setUpdatingBlingName(true);
    try {
      await api.patch(`/bling/contact/${contact.id}/update-bling`, {
        nome: contact.name,
        celular: contact.number || undefined,
      });
      toast.success("Nome e telefone atualizados no Bling!");
      setBlingData(d => d ? { ...d, blingContact: { ...d.blingContact, nome: contact.name } } : d);
    } catch (err) { toastError(err); }
    finally { setUpdatingBlingName(false); }
  };

  // Espelho Bling→chat (Fase 3): traz nome/e-mail do Bling para o contato local.
  // Bling não emite webhook de contato → pull sob demanda. O contato atualiza via
  // socket "contact" emitido pelo backend.
  const handleRefreshFromBling = async () => {
    if (!contact?.id) return;
    setUpdatingBlingName(true);
    try {
      const { data } = await api.post(`/bling/contact/${contact.id}/refresh`);
      if (data?.changed?.length) toast.success(`Atualizado do Bling: ${data.changed.join(", ")}.`);
      else toast.info("O contato já está igual ao Bling.");
    } catch (err) { toastError(err); }
    finally { setUpdatingBlingName(false); }
  };

  const loadFiscalData = async (blingContactId) => {
    if (!blingContactId || fiscalLoaded) return;
    setFiscalLoading(true);
    try {
      const { data } = await api.get(`/bling/contact-detail/${blingContactId}`);
      if (data) setFiscalData({
        cpf: data.cpf || "", cnpj: data.cnpj || "",
        email: data.email || "", celular: data.celular || "",
        dataNascimento: data.dataNascimento || "",
        logradouro: data.endereco?.logradouro || "",
        numero: data.endereco?.numero || "",
        complemento: data.endereco?.complemento || "",
        bairro: data.endereco?.bairro || "",
        municipio: data.endereco?.municipio || "",
        uf: data.endereco?.uf || "",
        cep: data.endereco?.cep || "",
      });
      setFiscalLoaded(true);
    } catch { /* silencioso */ }
    finally { setFiscalLoading(false); }
  };

  const handleSaveFiscal = async (resolveDuplicate = false) => {
    if (!contact?.id) return;
    setSavingFiscal(true);
    try {
      // Cliente novo (ainda sem vínculo Bling): cria o contato no Bling primeiro.
      // O create seta contact.blingContactId no banco, e o update-bling abaixo o lê.
      if (!blingData?.blingContact?.id && !resolveDuplicate) {
        await api.post(`/bling/contacts/create`, {
          contactId: contact.id,
          nome: contact.name,
          phone: contact.number,
        });
      }
      await api.patch(`/bling/contact/${contact.id}/update-bling`, {
        cpf: fiscalData.cpf || undefined,
        cnpj: fiscalData.cnpj || undefined,
        email: fiscalData.email || undefined,
        celular: fiscalData.celular || undefined,
        dataNascimento: fiscalData.dataNascimento || undefined,
        endereco: (fiscalData.logradouro || fiscalData.cep) ? {
          logradouro: fiscalData.logradouro || undefined,
          numero: fiscalData.numero || undefined,
          complemento: fiscalData.complemento || undefined,
          bairro: fiscalData.bairro || undefined,
          municipio: fiscalData.municipio || undefined,
          uf: fiscalData.uf || undefined,
          cep: fiscalData.cep || undefined,
        } : undefined,
        resolveDuplicate: resolveDuplicate || undefined,
      });
      toast.success(resolveDuplicate ? "Vínculo trocado e cópia excluída no Bling!" : "Dados salvos no Bling!");
      // Recarrega o painel Bling para refletir o vínculo recém-criado.
      setFiscalLoaded(false);
      api.get(`/bling/contact/${contact.id}/data`)
        .then(({ data }) => setBlingData(data))
        .catch(() => {});
    } catch (err) {
      // Dedup: já existe contato no Bling com esse CPF/CNPJ → oferecer troca de vínculo.
      if (err?.response?.status === 409 && err.response.data?.error === "DUPLICATE_DOCUMENT") {
        const ex = err.response.data.existing;
        setSavingFiscal(false);
        if (window.confirm(`Já existe um contato no Bling com esse CPF/CNPJ: "${ex?.nome || ex?.id}".\n\nTrocar o vínculo deste cliente para esse contato e excluir a cópia atual?`)) {
          return handleSaveFiscal(true);
        }
        return;
      }
      toastError(err);
    }
    finally { setSavingFiscal(false); }
  };

  const handleDiagnostic = async () => {
    if (!contact?.id) return;
    setDiagnosticLoading(true);
    setDiagnosticOpen(true);
    setDiagnosticData(null);
    try {
      const { data } = await api.get(`/bling/diagnostic/contact/${contact.id}`);
      setDiagnosticData(data);
    } catch (err) { toastError(err); setDiagnosticOpen(false); }
    finally { setDiagnosticLoading(false); }
  };

  const handleExtractFiscal = async () => {
    if (!contact?.id) return;
    setExtractingFiscal(true);
    setChatExtractFiscal(null);
    try {
      const { data } = await api.get(`/contacts/${contact.id}/extract-chat-data`);
      const fields = data?.fields || {};
      const hasAny = Object.keys(fields).some(k => fields[k]?.length > 0);
      setChatExtractFiscal(hasAny ? fields : {});
      setChatMessages(Array.isArray(data?.messages) ? data.messages : []);
    } catch (err) { toastError(err); }
    finally { setExtractingFiscal(false); }
  };

  // Copia um dado detectado no chat e dá feedback (usado no diálogo "Ver conversa").
  const copyDetected = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`Copiado: ${value}`);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };
  const DETECT_RE = /(\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2})|([\w.+-]+@[\w-]+\.[\w.-]+)|(\d{3}\.?\d{3}\.?\d{3}-?\d{2})|(\(?\d{2}\)?\s?9?\d{4}[-\s]?\d{4})|(\d{5}-?\d{3})/g;
  const highlightDetected = (text) => {
    if (!text) return null;
    const out = [];
    let last = 0;
    let m;
    DETECT_RE.lastIndex = 0;
    while ((m = DETECT_RE.exec(text)) !== null) {
      if (m.index > last) out.push(text.slice(last, m.index));
      const val = m[0];
      out.push(
        <span
          key={`${m.index}-${val}`}
          onClick={() => copyDetected(val.trim())}
          title="Clique para copiar"
          style={{ background: "rgba(243,112,33,0.15)", color: "#C05000", borderRadius: 4, padding: "0 3px", cursor: "pointer", fontWeight: 600 }}
        >
          {val}
        </span>
      );
      last = m.index + val.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  };

  const applyFiscalExtracted = (field, rawValue) => {
    let value = rawValue;
    if (field === "dataNascimento") {
      const [d, m, y] = rawValue.split("/");
      value = `${y}-${m?.padStart(2,"0")}-${d?.padStart(2,"0")}`;
    }
    setFiscalData(prev => ({ ...prev, [field]: value }));
    setChatExtractFiscal(prev => {
      if (!prev) return prev;
      const upd = { ...prev };
      // itens agora são { value, snippet } — filtra pelo valor já aplicado
      if (upd[field]) upd[field] = upd[field].filter(item => item.value !== rawValue);
      return upd;
    });
  };

  // Destaca o dado dentro do trecho da conversa (LGPD/Fase 2.1).
  const renderSnippet = (snippet, value) => {
    const idx = snippet.indexOf(value);
    if (idx === -1) return snippet;
    return (
      <>
        {snippet.slice(0, idx)}
        <mark style={{ background: "#fff3cd", padding: "0 2px", borderRadius: 2 }}>{value}</mark>
        {snippet.slice(idx + value.length)}
      </>
    );
  };

  const handleFiscalCepBlur = async () => {
    const cep = (fiscalData.cep || "").replace(/\D/g, "");
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await res.json();
      if (!d.erro) {
        setFiscalData(prev => ({
          ...prev,
          logradouro: prev.logradouro || d.logradouro || "",
          bairro: prev.bairro || d.bairro || "",
          municipio: prev.municipio || d.localidade || "",
          uf: prev.uf || d.uf || "",
        }));
      }
    } catch { /* silencioso */ }
    finally { setCepLoading(false); }
  };

  const setFiscal = (field) => (e) => setFiscalData(d => ({ ...d, [field]: e.target.value }));

  const FISCAL_EXTRACT_LABELS = { cpf:"CPF", cnpj:"CNPJ", email:"E-mail", celular:"Telefone", cep:"CEP", dataNascimento:"Data nasc." };
  const FISCAL_EXTRACT_FIELDS = ["cpf","cnpj","email","celular","cep","dataNascimento"];

  const handleAddMember = async () => {
    const num = addMemberNum.replace(/\D/g, "");
    if (!num || !whatsappId || !contact?.number) return;
    const groupId = `${contact.number}@g.us`;
    try {
      await api.post(`/groups/${encodeURIComponent(groupId)}/members`, { whatsappId, participants: [num] });
      setAddMemberNum("");
      await loadGroupMembers();
    } catch (err) {
      toastError(err);
    }
  };

  const handleRemoveMember = async (participantId) => {
    if (!whatsappId || !contact?.number) return;
    const groupId = `${contact.number}@g.us`;
    const num = participantId.replace("@c.us", "").replace("@lid", "");
    try {
      await api.delete(`/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(num)}`, {
        data: { whatsappId },
      });
      await loadGroupMembers();
    } catch (err) {
      toastError(err);
    }
  };

  const handlePromoteDemote = async (participantId, isCurrentlyAdmin) => {
    if (!whatsappId || !contact?.number) return;
    const groupId = `${contact.number}@g.us`;
    const num = participantId.replace("@c.us", "").replace("@lid", "");
    const action = isCurrentlyAdmin ? "demote" : "promote";
    try {
      await api.post(`/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(num)}/${action}`, { whatsappId });
      await loadGroupMembers();
      toast.success(isCurrentlyAdmin ? "Admin removido." : "Promovido a admin!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleLeaveGroup = async () => {
    if (!whatsappId || !contact?.number) return;
    const groupId = `${contact.number}@g.us`;
    setLeavingGroup(true);
    try {
      await api.post(`/groups/${encodeURIComponent(groupId)}/leave`, { whatsappId });
      toast.success("Saiu do grupo.");
      handleDrawerClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLeavingGroup(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!whatsappId || !contact?.number) return;
    const groupId = `${contact.number}@g.us`;
    try {
      const { data } = await api.get(`/groups/${encodeURIComponent(groupId)}/invite-link`, {
        params: { whatsappId },
      });
      await navigator.clipboard.writeText(data.link);
      toast?.success?.("Link copiado!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleRevokeInviteLink = async () => {
    if (!whatsappId || !contact?.number) return;
    const groupId = `${contact.number}@g.us`;
    try {
      const { data } = await api.delete(`/groups/${encodeURIComponent(groupId)}/invite-link`, {
        data: { whatsappId },
      });
      await navigator.clipboard.writeText(data.link);
      toast.success("Link revogado e novo link copiado!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveGroupSubject = async () => {
    if (!whatsappId || !contact?.number || !groupSubject.trim()) return;
    const groupId = `${contact.number}@g.us`;
    setSavingGroupSettings(true);
    try {
      await api.put(`/groups/${encodeURIComponent(groupId)}/subject`, { whatsappId, subject: groupSubject.trim() });
      toast.success("Nome do grupo atualizado!");
      setEditingSubject(false);
    } catch (err) {
      toastError(err);
    } finally {
      setSavingGroupSettings(false);
    }
  };

  const handleSaveGroupDescription = async () => {
    if (!whatsappId || !contact?.number) return;
    const groupId = `${contact.number}@g.us`;
    setSavingGroupSettings(true);
    try {
      await api.put(`/groups/${encodeURIComponent(groupId)}/description`, { whatsappId, description: groupDescription });
      toast.success("Descrição do grupo atualizada!");
      setEditingDescription(false);
    } catch (err) {
      toastError(err);
    } finally {
      setSavingGroupSettings(false);
    }
  };

  const handleToggleAdminsOnly = async (value) => {
    if (!whatsappId || !contact?.number) return;
    const groupId = `${contact.number}@g.us`;
    try {
      await api.put(`/groups/${encodeURIComponent(groupId)}/admins-only`, { whatsappId, adminsOnly: value });
      setAdminsOnly(value);
      toast.success(value ? "Só admins podem enviar mensagens." : "Todos podem enviar mensagens.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleToggleTemporary = async (value) => {
    if (!whatsappId || !contact?.number) return;
    const groupId = `${contact.number}@g.us`;
    try {
      await api.put(`/groups/${encodeURIComponent(groupId)}/temporary`, { whatsappId, enabled: value });
      setTemporaryMessages(value);
      toast.success(value ? "Mensagens temporárias ativadas (7 dias)." : "Mensagens temporárias desativadas.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleJoinGroup = async () => {
    if (!whatsappId || !joinLinkValue.trim()) return;
    try {
      await api.post("/groups/join", { whatsappId, inviteLink: joinLinkValue.trim() });
      toast.success("Entrou no grupo!");
      setJoinLinkOpen(false);
      setJoinLinkValue("");
    } catch (err) {
      toastError(err);
    }
  };

  const handleApplyLabel = async (labelId) => {
    if (!whatsappId || !contact?.number) return;
    const chatId = `${contact.number}@c.us`;
    setApplyingLabel(labelId);
    try {
      await api.post(`/whatsapp/${whatsappId}/labels`, { chatId, labelIds: [labelId], action: "add" });
      toast.success("Etiqueta aplicada no WhatsApp");
    } catch (err) {
      toastError(err);
    } finally {
      setApplyingLabel(null);
    }
  };

  const handleLoadBizProfile = async () => {
    if (!whatsappId || !contact?.id) return;
    setBizLoading(true);
    try {
      const { data } = await api.get(`/contacts/${contact.id}/business-profile`, { params: { whatsappId } });
      setBusinessProfile(data);
    } catch {
      setBusinessProfile(null);
    } finally {
      setBizLoading(false);
    }
  };

  const handleToggleBlock = async () => {
    if (!whatsappId || !contact?.id) return;
    setBlockLoading(true);
    try {
      const action = blocked ? "unblock" : "block";
      await api.post(`/contacts/${contact.id}/${action}`, { whatsappId });
      setBlocked(!blocked);
    } catch (err) {
      toastError(err);
    } finally {
      setBlockLoading(false);
    }
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={open}
      PaperProps={{ style: { position: "absolute" } }}
      BackdropProps={{ style: { position: "absolute" } }}
      ModalProps={{
        container: document.getElementById("drawer-container"),
        style: { position: "absolute" },
      }}
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.header}>
        <IconButton onClick={handleDrawerClose}>
          <CloseIcon />
        </IconButton>
        <Typography style={{ justifySelf: "center" }}>
          {i18n.t("contactDrawer.header")}
        </Typography>
      </div>
      {loading ? (
        <ContactDrawerSkeleton classes={classes} />
      ) : (
        <>
        <div className={classes.content}>
          <Paper square variant="outlined" className={classes.contactHeader}>
            <Avatar
              alt={contact.name}
              src={contact.profilePicUrl}
              className={classes.contactAvatar}
              onClick={() => contact.profilePicUrl && setPhotoPreviewOpen(true)}
              style={{ cursor: contact.profilePicUrl ? "zoom-in" : "default" }}
            />
            {contact.profilePicUrl && (
              <Dialog
                open={photoPreviewOpen}
                onClose={() => setPhotoPreviewOpen(false)}
                maxWidth={false}
                PaperProps={{ style: { background: "transparent", boxShadow: "none", margin: 8 } }}
                onClick={() => setPhotoPreviewOpen(false)}
              >
                <img
                  src={contact.profilePicUrl}
                  alt={contact.name}
                  style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8, display: "block" }}
                />
              </Dialog>
            )}
            <Typography>{contact.name}</Typography>
            <Typography>
              <Link href={`tel:${contact.number}`}>{contact.number}</Link>
            </Typography>
            {contactAbout && (
              <Typography variant="body2" style={{ marginTop: 4, fontStyle: "italic", color: "#8A9096", maxWidth: 260, textAlign: "center" }}>
                💬 {contactAbout}
              </Typography>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setModalOpen(true)}
              >
                {i18n.t("contactDrawer.buttons.edit")}
              </Button>
              {whatsappId && (
                <Button
                  variant="outlined"
                  color={blocked ? "primary" : "error"}
                  startIcon={<BlockIcon />}
                  onClick={handleToggleBlock}
                  disabled={blockLoading}
                  size="small"
                >
                  {blocked ? "Desbloquear" : "Bloquear"}
                </Button>
              )}
            </div>
          </Paper>

          {/* Arquivos do cliente no Google Drive (fase 2 / E1) */}
          {!contact.isGroup && (
            <Paper square variant="outlined" className={classes.tagsSection}>
              <div className={classes.tagsSectionTitle}>
                <FolderIcon style={{ fontSize: 16 }} />
                <Typography variant="subtitle2">Arquivos (Drive)</Typography>
              </div>
              <DriveExplorer contact={contact} />
            </Paper>
          )}

          {/* Tags */}
          <Paper square variant="outlined" className={classes.tagsSection}>
            <div className={classes.tagsSectionTitle}>
              <LocalOfferIcon style={{ fontSize: 16 }} />
              <Typography variant="subtitle2">Tags</Typography>
              <IconButton size="small" onClick={() => setShowAddTag(v => !v)}>
                <AddIcon style={{ fontSize: 16 }} />
              </IconButton>
            </div>
            <div className={classes.tagsContainer}>
              {tags.map(tag => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  onDelete={() => handleDeleteTag(tag.id)}
                  style={{
                    backgroundColor: tag.color,
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
              ))}
              {tags.length === 0 && (
                <Typography variant="caption" style={{ color: "#8A9096" }}>
                  Nenhuma tag
                </Typography>
              )}
            </div>
            {showAddTag && (
              <>
                <div className={classes.addTagRow}>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={allTags}
                    value={tagInput}
                    getOptionLabel={(opt) =>
                      typeof opt === "string" ? opt : opt.name || ""
                    }
                    filterOptions={(options, params) => {
                      const filtered = tagFilter(options, params);
                      const inputVal = params.inputValue.trim();
                      if (
                        inputVal &&
                        !options.find(o => o.name.toLowerCase() === inputVal.toLowerCase())
                      ) {
                        filtered.push({ name: inputVal, color: tagInputColor, isNew: true });
                      }
                      return filtered;
                    }}
                    renderOption={(props, opt) => (
                      <li {...props} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: opt.isNew ? tagInputColor : opt.color,
                            flexShrink: 0,
                          }}
                        />
                        {opt.isNew ? `Criar: "${opt.name}"` : opt.name}
                      </li>
                    )}
                    onChange={(_, value) => {
                      if (!value) {
                        setTagInput(null);
                        setTagInputColor("#F37021");
                        return;
                      }
                      if (typeof value === "string") {
                        setTagInput(value);
                        setTagInputColor("#F37021");
                      } else {
                        setTagInput(value);
                        setTagInputColor(value.isNew ? tagInputColor : value.color);
                      }
                    }}
                    onInputChange={(_, val) => {
                      if (typeof tagInput !== "object" || !tagInput) {
                        setTagInput(val);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Buscar ou criar tag..."
                        onKeyPress={e => e.key === "Enter" && handleAddTag()}
                        style={{ flex: 1 }}
                      />
                    )}
                    style={{ flex: 1 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleAddTag}
                  >
                    OK
                  </Button>
                </div>
                {isNewTag(tagInput) && (
                  <div className={classes.colorDots}>
                    {TAG_COLORS.map(c => (
                      <div
                        key={c}
                        className={classes.colorDot}
                        style={{
                          backgroundColor: c,
                          border: tagInputColor === c ? "2px solid #333" : "2px solid transparent",
                        }}
                        onClick={() => setTagInputColor(c)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </Paper>

          <Paper square variant="outlined" className={classes.contactDetails}>
            <ContactModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              contactId={contact.id}
            />
            <Typography variant="subtitle1">
              {i18n.t("contactDrawer.extraInfo")}
            </Typography>
            {contact?.extraInfo?.map(info => (
              <Paper
                key={info.id}
                square
                variant="outlined"
                className={classes.contactExtraInfo}
              >
                <InputLabel>{info.name}</InputLabel>
                <Typography component="div" noWrap style={{ paddingTop: 2 }}>
                  <MarkdownWrapper>{info.value}</MarkdownWrapper>
                </Typography>
              </Paper>
            ))}
          </Paper>

          {!isGroup && whatsappId && waLabels.length > 0 && (
            <Paper square variant="outlined" style={{ marginTop: 8, padding: 8 }}>
              <Typography variant="subtitle2" style={{ color: "#8A9096", marginBottom: 6 }}>🏷 Etiquetas WA</Typography>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {waLabels.map(label => (
                  <Tooltip key={label.id} title={`Aplicar "${label.name}" a este chat`}>
                    <Chip
                      label={label.name}
                      size="small"
                      onClick={() => handleApplyLabel(label.id)}
                      disabled={applyingLabel === label.id}
                      style={{
                        backgroundColor: label.color || "#cccccc",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 11,
                        cursor: "pointer",
                        opacity: applyingLabel === label.id ? 0.6 : 1
                      }}
                    />
                  </Tooltip>
                ))}
              </div>
              <Typography variant="caption" style={{ color: "#aaa", display: "block", marginTop: 4 }}>
                Clique na etiqueta para aplicar ao chat do WhatsApp
              </Typography>
            </Paper>
          )}

          {!isGroup && whatsappId && (
            <Paper square variant="outlined" style={{ marginTop: 8, padding: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <Typography variant="subtitle2" style={{ color: "#8A9096" }}>Perfil Empresarial</Typography>
                <Button size="small" onClick={handleLoadBizProfile} disabled={bizLoading}>
                  {bizLoading ? "Carregando..." : "Verificar"}
                </Button>
              </div>
              {businessProfile ? (
                <div style={{ fontSize: 13 }}>
                  {businessProfile.description && <Typography variant="body2">{businessProfile.description}</Typography>}
                  {businessProfile.email && <Typography variant="caption" display="block">✉️ {businessProfile.email}</Typography>}
                  {businessProfile.website?.length > 0 && <Typography variant="caption" display="block">🌐 {businessProfile.website[0]}</Typography>}
                  {businessProfile.category && <Typography variant="caption" display="block">🏷 {businessProfile.category}</Typography>}
                </div>
              ) : bizLoading ? null : (
                <Typography variant="caption" style={{ color: "#8A9096" }}>Clique em "Verificar" para carregar</Typography>
              )}
            </Paper>
          )}

          {!isGroup && blingEnabled && ["admin", "supervisor"].includes(user?.profile) && (
            <Paper square variant="outlined" style={{ marginTop: 8, padding: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
                <Typography variant="subtitle2" style={{ color: "#8A9096", fontSize: 12 }}>Pedidos Bling</Typography>
                {blingLoading && <Typography variant="caption" style={{ color: "#8A9096", marginLeft: "auto" }}>Carregando...</Typography>}
              </div>
              {blingData?.found && contact?.name &&
                blingData.blingContact?.nome?.trim().toLowerCase() !== contact.name.trim().toLowerCase() &&
                ["admin", "supervisor"].includes(user?.profile) && (
                <div style={{ background: "#fff8f3", border: "1px solid #F37021", borderRadius: 4, padding: "6px 8px", marginBottom: 6 }}>
                  <Typography variant="caption" style={{ display: "block", color: "#8A9096", marginBottom: 2 }}>
                    Nome no Bling: <strong>{blingData.blingContact.nome}</strong>
                  </Typography>
                  <Typography variant="caption" style={{ display: "block", color: "#333", marginBottom: 4 }}>
                    Nome no chat: <strong>{contact.name}</strong>
                  </Typography>
                  <div style={{ display: "flex", gap: 4 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      fullWidth
                      disabled={updatingBlingName}
                      onClick={handleUpdateBlingName}
                      style={{ fontSize: 11, color: "#F37021", borderColor: "#F37021", padding: "2px 8px" }}
                    >
                      {updatingBlingName ? "..." : "→ Enviar ao Bling"}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      fullWidth
                      disabled={updatingBlingName}
                      onClick={handleRefreshFromBling}
                      style={{ fontSize: 11, color: "#1976d2", borderColor: "#1976d2", padding: "2px 8px" }}
                    >
                      {updatingBlingName ? "..." : "← Trazer do Bling"}
                    </Button>
                  </div>
                </div>
              )}
              {blingData && !blingData.found && !blingLoading && (
                <div>
                  <Typography variant="caption" style={{ color: "#8A9096", display: "block", marginBottom: 4 }}>
                    Contato não encontrado pelo telefone.
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar por nome no Bling…"
                    value={blingSearch}
                    onChange={e => handleBlingSearch(e.target.value)}
                    inputProps={{ style: { fontSize: 12 } }}
                    style={{ marginBottom: 4 }}
                  />
                  {blingSearchLoading && (
                    <Typography variant="caption" style={{ color: "#8A9096" }}>Buscando…</Typography>
                  )}
                  {blingSearchResults.map(c => (
                    <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 0", borderBottom: "1px solid #f5f5f5" }}>
                      <div>
                        <Typography variant="caption" style={{ fontWeight: 600 }}>{c.nome}</Typography>
                        {c.celular && <Typography variant="caption" display="block" style={{ color: "#8A9096" }}>{c.celular}</Typography>}
                      </div>
                      <Button size="small" variant="outlined" style={{ fontSize: 10, padding: "1px 6px", minWidth: "auto" }} onClick={() => handleLinkBlingContact(c.id)}>
                        Vincular
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {blingData?.orders?.length > 0 && blingData.orders.map(order => (
                <div key={order.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid #f0f0f0" }}>
                  <div>
                    <Typography variant="caption" style={{ fontWeight: 600 }}>#{order.numero}</Typography>
                    <Typography variant="caption" display="block" style={{ color: "#555A5F" }}>{order.situacao?.nome ?? "—"}</Typography>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Typography variant="caption" style={{ color: "#273A74" }}>
                      {(order.total ?? order.totalProdutos ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                    </Typography>
                    {ticketId && (
                      <Tooltip title="Enviar pedido no chat">
                        <IconButton size="small" onClick={() => handleSendBlingOrder(order.id)} style={{ padding: 2 }}>
                          <Typography style={{ fontSize: 14, lineHeight: 1 }}>📤</Typography>
                        </IconButton>
                      </Tooltip>
                    )}
                    {ticketId && order.hasNF && (
                      <Tooltip title="Enviar NF no chat">
                        <IconButton size="small" onClick={() => handleSendBlingNF(order.id)} style={{ padding: 2 }}>
                          <Typography style={{ fontSize: 14, lineHeight: 1 }}>🧾</Typography>
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
              ))}
              {/* LGPD/Fase 3.3: situação financeira do cliente (dívida) só para
                  admin/supervisor — dado sensível que não deve aparecer para o
                  atendente comum. */}
              {blingData?.receivables?.length > 0 && ["admin", "supervisor"].includes(user?.profile) && (
                <div style={{ marginTop: 6 }}>
                  <Typography variant="caption" style={{ color: "#e53935", fontWeight: 600 }}>
                    Contas em aberto: {blingData.receivables.reduce((s, r) => s + (r.valor || 0), 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                  </Typography>
                </div>
              )}
              {!blingLoading && !blingData && (
                <Typography variant="caption" style={{ color: "#8A9096" }}>Sem dados.</Typography>
              )}
            </Paper>
          )}

          {/* ── Dados Fiscais Bling (inline) ──
              LGPD/Fase 2.2: dados pessoais (CPF/CNPJ/endereço) só para admin/supervisor.
              Aparece tanto para contato JÁ vinculado (editar) quanto para cliente NOVO
              (cadastrar) — neste caso "Salvar no Bling" cria o contato. Accordion fechado
              por padrão; dados só carregam ao expandir, evitando exibir PII o tempo todo. */}
          {!isGroup && blingEnabled && blingData && !blingLoading && ["admin", "supervisor"].includes(user?.profile) && (
            <Accordion
              square
              variant="outlined"
              style={{ marginTop: 8 }}
              onChange={(_, expanded) => { if (expanded && blingData?.blingContact?.id) loadFiscalData(blingData.blingContact.id); }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ minHeight: 36, padding: "0 10px" }}>
                <Typography variant="caption" style={{ fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: .5 }}>
                  {blingData?.found ? "Dados Fiscais" : "Cadastrar no Bling"} {fiscalLoading && <CircularProgress size={10} style={{ marginLeft: 6 }} />}
                </Typography>
              </AccordionSummary>
              <AccordionDetails style={{ padding: "8px 10px 12px" }}>
                {/* Buscar no chat — mostra o TRECHO da conversa com o dado destacado,
                    para o operador conferir o contexto antes de usar (LGPD/Fase 2.1) */}
                <div style={{ display:"flex", gap:6, marginBottom:8, flexWrap:"wrap" }}>
                  <Button size="small" variant="outlined"
                    startIcon={extractingFiscal ? <CircularProgress size={12}/> : <FindInPageIcon style={{fontSize:14}}/>}
                    onClick={handleExtractFiscal} disabled={extractingFiscal}
                    style={{ fontSize:10, borderColor:"#1565c0", color:"#1565c0" }}>
                    {extractingFiscal ? "Buscando..." : "Buscar dados no chat"}
                  </Button>
                  {chatMessages.length > 0 && (
                    <Button size="small" variant="text"
                      onClick={() => setChatViewOpen(true)}
                      style={{ fontSize:10, color:"#555" }}>
                      Ver conversa ({chatMessages.length})
                    </Button>
                  )}
                </div>

                {/* Trechos da conversa com os dados encontrados (operador confere e usa) */}
                {chatExtractFiscal !== null && (
                  <div style={{ marginBottom: 8 }}>
                    {Object.keys(chatExtractFiscal).filter(k => chatExtractFiscal[k]?.length > 0).length === 0 ? (
                      <Typography variant="caption" style={{ color:"#888" }}>Nenhum dado encontrado nas mensagens.</Typography>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        {FISCAL_EXTRACT_FIELDS.filter(f => chatExtractFiscal[f]?.length > 0).map(field =>
                          chatExtractFiscal[field].map((item, i) => (
                            <div key={`${field}-${i}`} style={{ border:"1px solid #d3d9e0", borderRadius:6, padding:"6px 8px", background:"#f7f9fc" }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:6 }}>
                                <Typography variant="caption" style={{ fontWeight:700, color:"#1565c0" }}>
                                  {FISCAL_EXTRACT_LABELS[field]}: {item.value}
                                </Typography>
                                <Button size="small" variant="text" onClick={() => applyFiscalExtracted(field, item.value)}
                                  style={{ fontSize:10, minWidth:0, padding:"0 6px", color:"#1565c0" }}>
                                  Usar
                                </Button>
                              </div>
                              <Typography variant="caption" component="div" style={{ color:"#666", marginTop:2, lineHeight:1.3 }}>
                                {renderSnippet(item.snippet, item.value)}
                              </Typography>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField size="small" fullWidth label="CPF" value={fiscalData.cpf} onChange={setFiscal("cpf")}
                      placeholder="000.000.000-00" inputProps={{style:{fontSize:12}}} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField size="small" fullWidth label="CNPJ" value={fiscalData.cnpj} onChange={setFiscal("cnpj")}
                      placeholder="00.000.000/0000-00" inputProps={{style:{fontSize:12}}} />
                  </Grid>
                  <Grid item xs={7}>
                    <TextField size="small" fullWidth label="E-mail" value={fiscalData.email} onChange={setFiscal("email")}
                      inputProps={{style:{fontSize:12}}} />
                  </Grid>
                  <Grid item xs={5}>
                    <TextField size="small" fullWidth label="Celular" value={fiscalData.celular} onChange={setFiscal("celular")}
                      inputProps={{style:{fontSize:12}}} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField size="small" fullWidth label="Data de nascimento" value={fiscalData.dataNascimento}
                      onChange={setFiscal("dataNascimento")} type="date" InputLabelProps={{shrink:true}} inputProps={{style:{fontSize:12}}} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField size="small" fullWidth label="CEP" value={fiscalData.cep}
                      onChange={setFiscal("cep")} onBlur={handleFiscalCepBlur}
                      placeholder="00000-000"
                      inputProps={{style:{fontSize:12}}}
                      InputProps={cepLoading ? { endAdornment: <CircularProgress size={12}/> } : {}} />
                  </Grid>
                  <Grid item xs={8}>
                    <TextField size="small" fullWidth label="Logradouro" value={fiscalData.logradouro} onChange={setFiscal("logradouro")}
                      inputProps={{style:{fontSize:12}}} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField size="small" fullWidth label="Número" value={fiscalData.numero} onChange={setFiscal("numero")}
                      inputProps={{style:{fontSize:12}}} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField size="small" fullWidth label="Bairro" value={fiscalData.bairro} onChange={setFiscal("bairro")}
                      inputProps={{style:{fontSize:12}}} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField size="small" fullWidth label="Cidade" value={fiscalData.municipio} onChange={setFiscal("municipio")}
                      inputProps={{style:{fontSize:12}}} />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField size="small" fullWidth label="UF" value={fiscalData.uf} onChange={setFiscal("uf")}
                      inputProps={{maxLength:2, style:{fontSize:12, textTransform:"uppercase"}}} />
                  </Grid>
                  <Grid item xs={12} style={{ marginTop: 4 }}>
                    <Button size="small" variant="contained"
                      onClick={() => handleSaveFiscal()} disabled={savingFiscal}
                      startIcon={savingFiscal ? <CircularProgress size={12} style={{color:"#fff"}}/> : <SaveIcon style={{fontSize:14}}/>}
                      style={{ backgroundColor:"#1565c0", color:"#fff", fontSize:11 }}>
                      {blingData?.found ? "Salvar no Bling" : "Cadastrar no Bling"}
                    </Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Dialog "Ver conversa" — conversa recente (ambos os lados) para o operador
              copiar/colar manualmente o que a extração automática não pegou. */}
          <Dialog open={chatViewOpen} onClose={() => setChatViewOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle style={{ fontSize: 14, paddingBottom: 4 }}>
              Conversa — {contact?.name}
            </DialogTitle>
            <DialogContent dividers style={{ padding: "8px 12px", maxHeight: "60vh" }}>
              {chatMessages.length === 0 ? (
                <Typography variant="body2" style={{ color: "#888" }}>Sem mensagens.</Typography>
              ) : (
                <>
                  <Typography variant="caption" style={{ color: "#888", display: "block", marginBottom: 6 }}>
                    Dados detectados aparecem destacados — clique para copiar.
                  </Typography>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {chatMessages.map((m, i) => (
                      <div key={i} style={{
                        alignSelf: m.fromMe ? "flex-end" : "flex-start",
                        maxWidth: "88%",
                        background: m.fromMe ? "#dcf8c6" : "#f0f0f0",
                        borderRadius: 8, padding: "7px 10px",
                      }}>
                        <Typography variant="body2" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#222", lineHeight: 1.4 }}>
                          {highlightDetected(m.body)}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button size="small" onClick={() => setChatViewOpen(false)}>Fechar</Button>
            </DialogActions>
          </Dialog>

          {/* Botão de diagnóstico de sincronização (admin/supervisor, só quando vinculado ao Bling) */}
          {!isGroup && blingEnabled && blingData?.found && ["admin", "supervisor"].includes(user?.profile) && (
            <div style={{ padding: "4px 0 2px" }}>
              <Button size="small" fullWidth variant="outlined"
                onClick={handleDiagnostic} disabled={diagnosticLoading}
                startIcon={diagnosticLoading ? <CircularProgress size={12}/> : null}
                style={{ fontSize: 10, borderColor: "#888", color: "#555", textTransform: "none" }}>
                {diagnosticLoading ? "Verificando..." : "Verificar sincronização ChatKR ↔ Bling"}
              </Button>
            </div>
          )}

          {/* Dialog de diagnóstico */}
          <Dialog open={diagnosticOpen} onClose={() => setDiagnosticOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle style={{ fontSize: 14, paddingBottom: 4 }}>
              Diagnóstico de Sincronização — {contact?.name}
            </DialogTitle>
            <DialogContent dividers style={{ padding: "8px 16px" }}>
              {diagnosticLoading && <div style={{ textAlign:"center", padding: 24 }}><CircularProgress size={32}/></div>}
              {diagnosticData && !diagnosticData.linked && (
                <Typography variant="body2" style={{ color: "#888" }}>Contato não vinculado ao Bling.</Typography>
              )}
              {diagnosticData?.linked && (
                <>
                  {/* Campos ChatKR vs Bling */}
                  <Typography variant="caption" style={{ fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: .5, display: "block", marginBottom: 6 }}>
                    Campos — ChatKR vs Bling
                  </Typography>
                  <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f5f5f5" }}>
                        <th style={{ textAlign:"left", padding:"3px 6px", fontWeight:600, color:"#444" }}>Campo</th>
                        <th style={{ textAlign:"left", padding:"3px 6px", fontWeight:600, color:"#1565c0" }}>ChatKR</th>
                        <th style={{ textAlign:"left", padding:"3px 6px", fontWeight:600, color:"#2e7d32" }}>Bling</th>
                        <th style={{ textAlign:"center", padding:"3px 6px", width:28 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diagnosticData.fields || []).map((f, i) => (
                        <tr key={i} style={{ borderBottom:"1px solid #eee", background: !f.ok ? "#fff3e0" : "transparent" }}>
                          <td style={{ padding:"3px 6px", color:"#333", fontWeight: !f.ok ? 600 : 400 }}>{f.campo}</td>
                          <td style={{ padding:"3px 6px", color: f.chatKR ? "#222" : "#bbb", fontStyle: f.chatKR ? "normal" : "italic" }}>{f.chatKR || (f.nota || "—")}</td>
                          <td style={{ padding:"3px 6px", color: f.bling ? "#222" : "#bbb", fontStyle: f.bling ? "normal" : "italic" }}>{f.bling || (f.nota || "—")}</td>
                          <td style={{ textAlign:"center", padding:"3px 6px" }}>
                            <span style={{ color: f.ok ? "#2e7d32" : "#e65100", fontSize: 14 }}>{f.ok ? "✓" : "⚠"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Campos obrigatórios para NF-e */}
                  {diagnosticData.missingFields?.length > 0 && (
                    <div style={{ marginTop: 10, padding: "6px 10px", background: "#fff3e0", borderRadius: 4, border: "1px solid #ff9800" }}>
                      <Typography variant="caption" style={{ fontWeight: 700, color: "#e65100", display: "block" }}>
                        Campos obrigatórios para NF-e ausentes no Bling:
                      </Typography>
                      {diagnosticData.missingFields.map((f, i) => (
                        <Typography key={i} variant="caption" style={{ display: "block", color: "#bf360c" }}>• {f}</Typography>
                      ))}
                    </div>
                  )}
                  {diagnosticData.missingFields?.length === 0 && (
                    <div style={{ marginTop: 6, padding: "4px 8px", background: "#e8f5e9", borderRadius: 4 }}>
                      <Typography variant="caption" style={{ color: "#2e7d32" }}>Cadastro completo para emissão de NF-e.</Typography>
                    </div>
                  )}

                  {/* OS vinculadas */}
                  {diagnosticData.linkedOrders?.length > 0 && (
                    <>
                      <Typography variant="caption" style={{ fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: .5, display: "block", marginTop: 12, marginBottom: 6 }}>
                        OS / Pedidos no Bling
                      </Typography>
                      {diagnosticData.linkedOrders.map((o, i) => (
                        <div key={i} style={{ padding: "6px 8px", marginBottom: 4, background: "#f9f9f9", borderRadius: 4, border: "1px solid #e0e0e0", fontSize: 11 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                            <span style={{ fontWeight: 600 }}>OS {o.numero}</span>
                            <span style={{ color: "#888" }}>{o.status || "—"}</span>
                          </div>
                          {o.blingOrderId ? (
                            <div style={{ color: "#444", marginTop: 2 }}>
                              <span>Bling #{o.blingOrderNumero || o.blingOrderId}</span>
                              {" · "}
                              <span style={{ color: o.itemsMatch ? "#2e7d32" : "#e65100" }}>
                                {o.itemsMatch ? `${o.localItemsCount} itens ✓` : `Itens: ChatKR=${o.localItemsCount} / Bling=${o.blingItemsCount} ⚠`}
                              </span>
                              {o.freteLocal > 0 || o.blingFrete > 0 ? (
                                <span style={{ color: o.freteLocal === o.blingFrete ? "#2e7d32" : "#e65100" }}>
                                  {" · "}Frete: ChatKR={o.freteLocal?.toLocaleString("pt-BR",{style:"currency",currency:"BRL"})} / Bling={(o.blingFrete||0)?.toLocaleString("pt-BR",{style:"currency",currency:"BRL"})} {o.freteLocal === o.blingFrete ? "✓" : "⚠"}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <div style={{ color: "#888", marginTop: 2 }}>Sem pedido no Bling</div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDiagnosticOpen(false)} size="small">Fechar</Button>
            </DialogActions>
          </Dialog>

          {!isGroup && ticketId && (
            <Paper square variant="outlined" style={{ marginTop: 8, padding: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <EngineeringIcon style={{ fontSize: 16, color: "#F37021" }} />
                <Typography variant="subtitle2" style={{ color: "#555", fontSize: 12, flex: 1 }}>Ordem de Serviço</Typography>
                <Tooltip title="Criar OS para este ticket">
                  <IconButton
                    size="small"
                    onClick={() => setSoDialogOpen(true)}
                    style={{ background: "#F37021", color: "#fff", borderRadius: 6, padding: "3px 8px", fontSize: 12 }}
                  >
                    <AddIcon style={{ fontSize: 14 }} />
                    <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 600 }}>Nova OS</span>
                  </IconButton>
                </Tooltip>
              </div>
            </Paper>
          )}

          {soDialogOpen && (
            <ServiceOrderDialog
              open={soDialogOpen}
              onClose={() => setSoDialogOpen(false)}
              onSaved={(entity) => {
                setSoDialogOpen(false);
                if (ticketId && entity) setSendConfirm({ type: "so", entity, printOpen: false });
              }}
              order={null}
              initialTicketId={ticketId}
            />
          )}

          {!isGroup && ticketId && (
            <Paper square variant="outlined" style={{ marginTop: 8, padding: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <RequestQuoteIcon style={{ fontSize: 16, color: "#273A74" }} />
                <Typography variant="subtitle2" style={{ color: "#555", fontSize: 12, flex: 1 }}>Orçamento</Typography>
                <Tooltip title="Criar orçamento para este ticket">
                  <IconButton
                    size="small"
                    onClick={() => setQuoteDialogOpen(true)}
                    style={{ background: "#273A74", color: "#fff", borderRadius: 6, padding: "3px 8px", fontSize: 12 }}
                  >
                    <AddIcon style={{ fontSize: 14 }} />
                    <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 600 }}>Novo ORC</span>
                  </IconButton>
                </Tooltip>
              </div>
            </Paper>
          )}

          {quoteDialogOpen && (
            <QuoteDialog
              open={quoteDialogOpen}
              onClose={() => setQuoteDialogOpen(false)}
              onSaved={(entity) => {
                setQuoteDialogOpen(false);
                if (ticketId && entity) setSendConfirm({ type: "quote", entity, printOpen: false });
              }}
              quote={null}
              initialTicketId={ticketId}
              initialContact={contact}
            />
          )}

          {!isGroup && ticketId && (
            <Paper square variant="outlined" style={{ marginTop: 8, padding: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <DescriptionOutlinedIcon style={{ fontSize: 16, color: "#2E7D32" }} />
                <Typography variant="subtitle2" style={{ color: "#555", fontSize: 12, flex: 1 }}>Receituário</Typography>
                <Tooltip title="Criar receituário para este ticket">
                  <IconButton
                    size="small"
                    onClick={() => setRecDialogOpen(true)}
                    style={{ background: "#2E7D32", color: "#fff", borderRadius: 6, padding: "3px 8px", fontSize: 12 }}
                  >
                    <AddIcon style={{ fontSize: 14 }} />
                    <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 600 }}>Novo REC</span>
                  </IconButton>
                </Tooltip>
              </div>
            </Paper>
          )}

          {!isGroup && ticketId && (
            <Paper square variant="outlined" style={{ marginTop: 8, padding: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <ContentCutOutlinedIcon style={{ fontSize: 16, color: "#00838F" }} />
                <Typography variant="subtitle2" style={{ color: "#555", fontSize: 12, flex: 1 }}>Adesivos</Typography>
                <Tooltip title="Gerar folha de adesivos para este ticket">
                  <IconButton
                    size="small"
                    onClick={() => setAdesivoDialogOpen(true)}
                    style={{ background: "#00838F", color: "#fff", borderRadius: 6, padding: "3px 8px", fontSize: 12, marginRight: 4 }}
                  >
                    <AddIcon style={{ fontSize: 14 }} />
                    <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 600 }}>Adesivo</span>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cartela: várias artes na folha">
                  <IconButton
                    size="small"
                    onClick={() => setCartelaDialogOpen(true)}
                    style={{ background: "#006064", color: "#fff", borderRadius: 6, padding: "3px 8px", fontSize: 12 }}
                  >
                    <AddIcon style={{ fontSize: 14 }} />
                    <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 600 }}>Cartela</span>
                  </IconButton>
                </Tooltip>
              </div>
            </Paper>
          )}

          {adesivoDialogOpen && (
            <AdesivoDialog
              open={adesivoDialogOpen}
              onClose={() => setAdesivoDialogOpen(false)}
              adesivoId={null}
              onSaved={() => {}}
              initialTicketId={ticketId}
              initialContact={contact}
            />
          )}

          {cartelaDialogOpen && (
            <CartelaDialog
              open={cartelaDialogOpen}
              onClose={() => setCartelaDialogOpen(false)}
              adesivoId={null}
              onSaved={() => {}}
              initialTicketId={ticketId}
              initialContact={contact}
            />
          )}

          {/* O receituário tem fluxo próprio (gerar -> enviar prova -> aprovar -> imprimir),
              por isso não entra no sendConfirm de OS/Orçamento: o envio da prova é feito
              de dentro do próprio diálogo, que já conhece o ticket. */}
          {recDialogOpen && (
            <ReceituarioDialog
              open={recDialogOpen}
              onClose={() => setRecDialogOpen(false)}
              receituarioId={null}
              onSaved={() => {}}
              initialTicketId={ticketId}
              initialContact={contact}
            />
          )}

          {/* Dialog de confirmação: enviar no chat ou imprimir PDF */}
          {sendConfirm && (
            <Dialog open onClose={() => setSendConfirm(null)} maxWidth="xs" fullWidth>
              <DialogTitle style={{ paddingBottom: 8 }}>
                {sendConfirm.type === "so" ? "🔧 OS criada!" : "📋 Orçamento criado!"}
              </DialogTitle>
              <DialogContent>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  {sendConfirm.type === "so"
                    ? `OS ${sendConfirm.entity.numero}`
                    : `Orçamento ${sendConfirm.entity.numero}`
                  }
                  {" "}para <strong>{sendConfirm.entity.clientName || contact?.name}</strong>.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Deseja enviar o PDF para o cliente no chat?
                </Typography>
              </DialogContent>
              <DialogActions style={{ padding: "8px 16px 16px", gap: 8, justifyContent: "space-between" }}>
                <Button onClick={() => setSendConfirm(null)} size="small">Não enviar</Button>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={() => setSendConfirm(prev => ({ ...prev, printOpen: true }))}
                  >
                    Ver PDF
                  </Button>
                  {sendConfirm.type === "so" && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<QrCode2Icon />}
                      style={{ color: "#00897b", borderColor: "#80cbc4" }}
                      onClick={() => {
                        setPixOrder({ ...sendConfirm.entity, ticketId: sendConfirm.entity.ticketId || ticketId });
                        setSendConfirm(null);
                      }}
                    >
                      Cobrar via Pix
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SendIcon />}
                    style={{ backgroundColor: "#F37021", color: "#fff" }}
                    onClick={async () => {
                      try {
                        const { entity, type } = sendConfirm;
                        if (type === "so") {
                          await api.post(`/service-orders/ticket/${ticketId}/send/${entity.id}`);
                        } else {
                          await api.post(`/quotes/ticket/${ticketId}/send/${entity.id}`);
                        }
                        toast.success("Enviado no chat!");
                        setSendConfirm(null);
                      } catch (err) {
                        toastError(err);
                      }
                    }}
                  >
                    Enviar resumo no chat
                  </Button>
                </div>
              </DialogActions>
            </Dialog>
          )}

          <PixChargeDialog
            open={Boolean(pixOrder)}
            order={pixOrder}
            onClose={() => setPixOrder(null)}
          />

          {/* PDF preview aberto a partir do dialog de confirmação */}
          {sendConfirm?.printOpen && sendConfirm.type === "so" && (
            <ServiceOrderPrint
              order={sendConfirm.entity}
              onClose={() => setSendConfirm(prev => ({ ...prev, printOpen: false }))}
            />
          )}
          {sendConfirm?.printOpen && sendConfirm.type === "quote" && (
            <QuotePrint
              quote={sendConfirm.entity}
              onClose={() => setSendConfirm(prev => ({ ...prev, printOpen: false }))}
            />
          )}

          {isGroup && whatsappId && (
            <>
            <Paper square variant="outlined" style={{ marginTop: 8, padding: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
                <GroupIcon style={{ fontSize: 16, color: "#8A9096" }} />
                <Typography variant="subtitle2" style={{ color: "#8A9096" }}>
                  Membros do grupo
                </Typography>
                <IconButton size="small" onClick={handleCopyInviteLink} style={{ marginLeft: "auto" }}>
                  <Tooltip title="Copiar link de convite">
                    <ContentCopyIcon style={{ fontSize: 16 }} />
                  </Tooltip>
                </IconButton>
              </div>
              {membersLoading ? (
                <Typography variant="caption" style={{ color: "#8A9096" }}>Carregando...</Typography>
              ) : (
                <List dense disablePadding>
                  {groupMembers.map((m) => {
                    const num = (m.id || "").replace("@c.us", "").replace("@lid", "");
                    const isAdmin = m.isAdmin || m.isSuperAdmin;
                    return (
                      <ListItem
                        key={m.id}
                        disableGutters
                        secondaryAction={
                          <div style={{ display: "flex", gap: 2 }}>
                            <Tooltip title={isAdmin ? "Remover admin" : "Tornar admin"}>
                              <IconButton size="small" onClick={() => handlePromoteDemote(m.id, isAdmin)}>
                                {isAdmin
                                  ? <StarIcon style={{ fontSize: 14, color: "#F37021" }} />
                                  : <StarBorderIcon style={{ fontSize: 14 }} />
                                }
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remover do grupo">
                              <IconButton size="small" onClick={() => handleRemoveMember(num)}>
                                <PersonRemoveIcon style={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        }
                      >
                        <ListItemText
                          primary={m.name || num}
                          secondary={isAdmin ? "Admin" : null}
                          primaryTypographyProps={{ variant: "body2", noWrap: true }}
                          secondaryTypographyProps={{ style: { color: "#F37021", fontSize: 10 } }}
                        />
                      </ListItem>
                    );
                  })}
                  {groupMembers.length === 0 && (
                    <Typography variant="caption" style={{ color: "#8A9096" }}>Nenhum membro</Typography>
                  )}
                </List>
              )}
              <Divider style={{ marginTop: 8, marginBottom: 8 }} />
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Número para adicionar"
                  value={addMemberNum}
                  onChange={e => setAddMemberNum(e.target.value)}
                  style={{ flex: 1 }}
                  onKeyPress={e => e.key === "Enter" && handleAddMember()}
                />
                <IconButton size="small" onClick={handleAddMember} color="primary">
                  <PersonAddIcon style={{ fontSize: 18 }} />
                </IconButton>
              </div>
              <Divider style={{ marginTop: 8, marginBottom: 8 }} />
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<ExitToAppIcon style={{ fontSize: 16 }} />}
                onClick={handleLeaveGroup}
                disabled={leavingGroup}
                fullWidth
              >
                {leavingGroup ? "Saindo..." : "Sair do grupo"}
              </Button>
              <Divider style={{ marginTop: 8, marginBottom: 8 }} />
              {/* Revogar link + opções de grupo */}
              <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                <Button size="small" variant="outlined" onClick={handleRevokeInviteLink} fullWidth>
                  Revogar link
                </Button>
                <Button size="small" variant="outlined" onClick={() => setJoinLinkOpen(true)} fullWidth>
                  Entrar por link
                </Button>
              </div>
              {/* Nome do grupo */}
              {editingSubject ? (
                <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 4 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Novo nome do grupo"
                    value={groupSubject}
                    onChange={e => setGroupSubject(e.target.value)}
                    style={{ flex: 1 }}
                    onKeyPress={e => e.key === "Enter" && handleSaveGroupSubject()}
                  />
                  <Button size="small" variant="contained" color="primary" onClick={handleSaveGroupSubject} disabled={savingGroupSettings}>
                    OK
                  </Button>
                  <Button size="small" onClick={() => setEditingSubject(false)}>✕</Button>
                </div>
              ) : (
                <Button size="small" variant="text" onClick={() => { setGroupSubject(contact?.name || ""); setEditingSubject(true); }} fullWidth>
                  Renomear grupo
                </Button>
              )}
              {/* Descrição do grupo */}
              {editingDescription ? (
                <div style={{ marginBottom: 4 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Descrição do grupo"
                    value={groupDescription}
                    onChange={e => setGroupDescription(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    style={{ marginBottom: 4 }}
                  />
                  <div style={{ display: "flex", gap: 4 }}>
                    <Button size="small" variant="contained" color="primary" onClick={handleSaveGroupDescription} disabled={savingGroupSettings}>
                      Salvar
                    </Button>
                    <Button size="small" onClick={() => setEditingDescription(false)}>Cancelar</Button>
                  </div>
                </div>
              ) : (
                <Button size="small" variant="text" onClick={() => { setGroupDescription(""); setEditingDescription(true); }} fullWidth>
                  Editar descrição
                </Button>
              )}
              {/* Toggles */}
              <div style={{ paddingLeft: 4 }}>
                <FormControlLabel
                  control={<Switch size="small" checked={adminsOnly} onChange={e => handleToggleAdminsOnly(e.target.checked)} />}
                  label={<span style={{ fontSize: 12 }}>Só admins enviam</span>}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={temporaryMessages} onChange={e => handleToggleTemporary(e.target.checked)} />}
                  label={<span style={{ fontSize: 12 }}>Msgs temporárias (7d)</span>}
                />
              </div>
            </Paper>

            {/* Dialog entrar por link */}
            <Dialog open={joinLinkOpen} onClose={() => setJoinLinkOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Entrar em grupo por link</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="https://chat.whatsapp.com/..."
                  value={joinLinkValue}
                  onChange={e => setJoinLinkValue(e.target.value)}
                  style={{ marginTop: 8 }}
                  onKeyPress={e => e.key === "Enter" && handleJoinGroup()}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setJoinLinkOpen(false)}>Cancelar</Button>
                <Button variant="contained" color="primary" onClick={handleJoinGroup}>Entrar</Button>
              </DialogActions>
            </Dialog>
            </>
          )}
        </div>

        {!isGroup && whatsappId && commonGroups.length > 0 && (
          <div style={{ padding: "0 8px 8px" }}>
            <Divider style={{ marginBottom: 8 }} />
            <InputLabel style={{ marginBottom: 6, fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
              Grupos em comum ({commonGroups.length})
            </InputLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {commonGroups.map(g => (
                <Typography key={g.id} variant="body2" style={{ fontSize: 13 }}>
                  👥 {g.name || g.id}
                </Typography>
              ))}
            </div>
          </div>
        )}

        {!isGroup && contactTickets.length > 0 && (
          <div style={{ padding: "0 8px 12px" }}>
            <Divider style={{ marginBottom: 8 }} />
            <InputLabel style={{ marginBottom: 6, fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
              Histórico de atendimentos
            </InputLabel>
            <List dense disablePadding>
              {contactTickets.slice(0, 10).map(t => {
                const statusColor = t.status === "closed" ? "#4caf50" : t.status === "open" ? "#F37021" : "#9e9e9e";
                const statusLabel = t.status === "closed" ? "Resolvido" : t.status === "open" ? "Em aberto" : "Pendente";
                return (
                  <ListItemButton
                    key={t.id}
                    disableGutters
                    style={{ padding: "2px 0" }}
                    onClick={() => navigate(`/tickets/${t.id}`)}
                  >
                    <ListItemText
                      primary={
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 12, color: statusColor, fontWeight: 600 }}>{statusLabel}</span>
                          {t.queue?.name && <span style={{ fontSize: 11, color: "#8A9096" }}>· {t.queue.name}</span>}
                        </span>
                      }
                      secondary={
                        <span style={{ fontSize: 11, color: "#8A9096" }}>
                          {t.lastMessage ? `${t.lastMessage.slice(0, 40)}${t.lastMessage.length > 40 ? "…" : ""}` : ""}
                          {t.updatedAt ? ` · ${format(new Date(t.updatedAt), "dd/MM HH:mm")}` : ""}
                        </span>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </div>
        )}
        </>
      )}
    </Drawer>
  );
};

export default ContactDrawer;
